import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { MdClose } from 'react-icons/md';
import Button from './Button';
import useEscapeKey from '../../hooks/useEscapeKey';
import useClubVocab from '../../hooks/useClubVocab';
import { recognizeStudent } from '../../services/gamificationService';

const RECOGNITION_BADGES = [
  { id: 'trainers_pick',      name: "Trainer's Pick",    icon: '🎖️', description: 'Outstanding performance recognized by trainer' },
  { id: 'best_effort',        name: 'Best Effort',       icon: '💫', description: 'Exceptional effort and dedication in training' },
  { id: 'respectful_student', name: 'Respectful Student',icon: '🙏', description: 'Respect and proper dojang etiquette' },
  { id: 'team_player',        name: 'Team Player',       icon: '🤝', description: 'Outstanding contribution to team spirit' },
  { id: 'leadership_spirit',  name: 'Leadership Spirit', icon: '🦅', description: 'Leadership qualities that inspire others' },
  { id: 'positive_attitude',  name: 'Positive Attitude', icon: '☀️', description: 'Positive energy that lifts the whole dojang' },
];

export default function RecognizeModal({ studentProfileId, studentName, onClose }) {
  const [selected, setSelected] = useState('');
  const [note, setNote]         = useState('');
  const qc                      = useQueryClient();
  const v                       = useClubVocab();

  const mutation = useMutation({
    mutationFn: () => recognizeStudent(studentProfileId, {
      badgeId: selected,
      note: note.trim() || undefined,
    }),
    onSuccess: (res) => {
      toast.success(res.data.message || 'Recognition awarded!');
      qc.invalidateQueries({ queryKey: ['student-full',            studentProfileId] });
      qc.invalidateQueries({ queryKey: ['student-gam-admin',       studentProfileId] });
      qc.invalidateQueries({ queryKey: ['trainer-student-profile', studentProfileId] });
      qc.invalidateQueries({ queryKey: ['trainer-progress-summary'] });
      onClose();
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Could not award recognition. Please try again.'),
  });

  const selectedBadge = RECOGNITION_BADGES.find(b => b.id === selected);

  useEscapeKey(onClose);

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Recognize ${v.member.toLowerCase()}`}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl">
          <div>
            <h2 className="font-bold text-secondary text-base">Recognize {v.member}</h2>
            <p className="text-xs text-gray-400 mt-0.5">{studentName}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100">
            <MdClose className="text-xl" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          {/* Info */}
          <p className="text-xs text-gray-500 bg-purple-50 border border-purple-100 rounded-xl px-3 py-2.5">
            Awards a recognition badge and <span className="font-semibold text-purple-700">+100 XP</span> to the {v.member.toLowerCase()}.
            Each badge can only be awarded once per {v.member.toLowerCase()}.
          </p>

          {/* Badge selection */}
          <div>
            <p className="text-xs font-semibold text-gray-600 mb-2">Select recognition type:</p>
            <div className="grid grid-cols-2 gap-2">
              {RECOGNITION_BADGES.map(b => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => setSelected(b.id)}
                  className={`flex items-start gap-2.5 p-3 rounded-xl border-2 text-left transition-all ${
                    selected === b.id
                      ? 'border-primary bg-primary/5 shadow-sm'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <span className="text-2xl flex-shrink-0 leading-none mt-0.5">{b.icon}</span>
                  <p className="text-xs font-semibold text-secondary leading-tight">{b.name}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Description of selected */}
          {selectedBadge && (
            <p className="text-xs text-gray-500 italic">{selectedBadge.description}</p>
          )}

          {/* Optional note */}
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1.5">
              Note for {v.member.toLowerCase()} <span className="font-normal text-gray-400">(optional)</span>
            </label>
            <textarea
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder="E.g. Great sparring today. Showed real discipline."
              rows={2}
              maxLength={200}
              className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-1">
            <Button variant="ghost" onClick={onClose} type="button">Cancel</Button>
            <Button
              onClick={() => mutation.mutate()}
              loading={mutation.isPending}
              disabled={!selected}
            >
              🎖️ Award Recognition
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
